import {
  checkboxClasses,
  filledInputClasses,
  formControlLabelClasses,
  formHelperTextClasses,
  formLabelClasses,
  iconButtonClasses,
  inputAdornmentClasses,
  inputBaseClasses,
  inputLabelClasses,
  radioClasses,
  svgIconClasses,
  typographyClasses,
  type Components,
} from '@mui/material';
import { themeVars } from '../themeVars';
import {
  inputAdornmentIconButtonVariables,
  inputAdornmentIconSizes,
  inputClearIconSizes,
  paletteColorNames,
  type Theme,
} from './utils';

/**
 * Date picker inputs do not use MUI's TextField component. Shared single-line metrics
 * are exported from this file so picker and standard inputs stay aligned.
 */

export const mediumSizeTypography = (theme: Theme) => ({
  fontSize: theme.typography.label2.fontSize,
  letterSpacing: theme.typography.label2.letterSpacing,
  lineHeight: theme.typography.label3.lineHeight,
});

export const smallSizeTypography = (theme: Theme) => ({
  fontSize: theme.typography.label3.fontSize,
  letterSpacing: theme.typography.label3.letterSpacing,
  lineHeight: theme.typography.label3.lineHeight,
});

export const filledInputSingleLineStyles = {
  medium: {
    paddingTop: 23,
    paddingBottom: 5,
  },
  mediumHiddenLabel: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  small: {
    paddingTop: 17,
    paddingBottom: 6,
  },
  smallHiddenLabel: {
    paddingTop: 8,
    paddingBottom: 7,
  },
} as const;

export const getFilledInputUnderlineStyles = ({
  theme,
  classes,
}: {
  theme: Theme;
  classes: { focused: string; disabled: string; error: string };
}) => ({
  // Purple bottom border for focused input. Use a subtler opacity transform instead
  // of MUI's recognizable scaleX transform.
  '&::after': {
    opacity: 0,
    transform: 'scaleX(1)',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeOut,
    }),
  },
  [`&.${classes.focused}:after`]: {
    opacity: 1,
    transform: 'scaleX(1)',
  },
  // Regular grey bottom border on non-focused inputs
  '&::before': {
    borderBottom: `1px solid ${themeVars(theme).palette.border.subtle}`,
  },
  [`&:hover:not(.${classes.disabled}, .${classes.error}):before`]: {
    borderBottomColor: themeVars(theme).palette.border.strong,
  },
});

export const MuiInputBase: Components<Theme>['MuiInputBase'] = {
  styleOverrides: {
    input: ({ theme }) => ({
      ...mediumSizeTypography(theme),

      // In a <Select> this input is just a div where we want to center text vertically to
      // match text field <input>
      display: 'inline-flex',
      alignItems: 'center',

      '&::placeholder': {
        opacity: 1,
        color: themeVars(theme).palette.text.secondary,
      },

      variants: [
        {
          props: { size: 'small' },
          style: smallSizeTypography(theme),
        },
      ],
    }),
  },
};

export const MuiFormControlLabel: Components<Theme>['MuiFormControlLabel'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginLeft: 0,
      gap: 6,

      [`&.${formControlLabelClasses.error} .${radioClasses.root}`]: {
        color: themeVars(theme).palette.error.main,
        '--mds-Radio-ringColor': themeVars(theme).palette.error.main,
      },

      [`&.${formControlLabelClasses.error} .${checkboxClasses.root}`]: {
        '--mds-Checkbox-ringColor': themeVars(theme).palette.error.main,
        [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
          '--mds-Checkbox-ringStyle': 'solid',
        },
      },
    }),
  },
};

export const MuiFormHelperText: Components<Theme>['MuiFormHelperText'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginLeft: 2,
      display: 'inline-flex',
      alignItems: 'center',

      [`&.${formHelperTextClasses.error}`]: {
        color: themeVars(theme).palette.text.error,
      },
    }),
  },
};

export const MuiInputAdornment: Components<Theme>['MuiInputAdornment'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...inputAdornmentIconButtonVariables('medium'),
      '--mds-icon-button-padding': theme.spacing(0.5),
      '--mds-icon-button-edge-end-margin': theme.spacing(-0.5),
      '--mds-icon-button-edge-start-margin': theme.spacing(-0.5),
      color: themeVars(theme).palette.text.secondary,

      [`&, & .${typographyClasses.root}`]: {
        ...mediumSizeTypography(theme),
      },

      [`& .${svgIconClasses.root}`]: {
        color: themeVars(theme).palette.text.secondary,
        fontSize: inputAdornmentIconSizes.medium,
        height: inputAdornmentIconSizes.medium,
        width: inputAdornmentIconSizes.medium,
      },
      [`& .MdsInputClearIcon, & :where(.clearButton) > .${svgIconClasses.root}`]: {
        fontSize: inputClearIconSizes.medium,
        height: inputClearIconSizes.medium,
        width: inputClearIconSizes.medium,
      },

      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        ...inputAdornmentIconButtonVariables('small'),
        [`&, & .${typographyClasses.root}`]: {
          ...smallSizeTypography(theme),
        },
        [`& .${svgIconClasses.root}`]: {
          fontSize: inputAdornmentIconSizes.small,
          height: inputAdornmentIconSizes.small,
          width: inputAdornmentIconSizes.small,
        },
        [`& .MdsInputClearIcon, & :where(.clearButton) > .${svgIconClasses.root}`]: {
          fontSize: inputClearIconSizes.small,
          height: inputClearIconSizes.small,
          width: inputClearIconSizes.small,
        },
      },
      [`.${inputBaseClasses.sizeSmall} & .${svgIconClasses.root}`]: {
        fontSize: inputAdornmentIconSizes.small,
        height: inputAdornmentIconSizes.small,
        width: inputAdornmentIconSizes.small,
      },
      [`.${inputBaseClasses.sizeSmall} &`]: {
        ...inputAdornmentIconButtonVariables('small'),
      },
      [`.${inputBaseClasses.sizeSmall} & .${iconButtonClasses.root} .${svgIconClasses.root}`]: {
        fontSize: inputAdornmentIconSizes.small,
        height: inputAdornmentIconSizes.small,
        width: inputAdornmentIconSizes.small,
      },
      [`.${inputBaseClasses.sizeSmall} & .${iconButtonClasses.root} .MdsInputClearIcon, .${inputBaseClasses.sizeSmall} & .MdsInputClearIcon, .${inputBaseClasses.sizeSmall} & :where(.clearButton) > .${svgIconClasses.root}`]:
        {
          fontSize: inputClearIconSizes.small,
          height: inputClearIconSizes.small,
          width: inputClearIconSizes.small,
        },
    }),
  },
  variants: [
    {
      props: {
        variant: 'filled',
      },
      style: {
        [`&.${inputAdornmentClasses.positionStart}&:not(.${inputAdornmentClasses.hiddenLabel})`]: {
          marginTop: 18,
          [`&.${inputAdornmentClasses.sizeSmall}`]: {
            marginTop: 12,
          },
        },
      },
    },
    {
      props: {
        position: 'start',
      },
      style: {
        marginRight: 6,
      },
    },
    {
      props: {
        position: 'end',
      },
      style: {
        marginLeft: 6,
      },
    },
  ],
};

export const MuiFormLabel: Components<Theme>['MuiFormLabel'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...mediumSizeTypography(theme),
      [`&.${inputLabelClasses.sizeSmall}`]: {
        ...smallSizeTypography(theme),
      },
    }),
  },
  variants: [
    ...paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => ({
        [`&.${formLabelClasses.focused}`]: {
          color: themeVars(theme).palette.text.secondary,
        },
      }),
    })),
    {
      props: {},
      style: ({ theme }) => ({
        [`&.${formLabelClasses.error}`]: {
          color: themeVars(theme).palette.text.secondary,
        },
      }),
    },
  ],
};

export const MuiInputLabel: Components<Theme>['MuiInputLabel'] = {
  variants: [
    {
      props: { variant: 'filled' },
      style: {
        transform: 'translate(12px, 16px) scale(1)',
      },
    },
    {
      props: ({ variant, ownerState }) => variant === 'filled' && !!ownerState.shrink,
      style: {
        transform: 'translate(12px, 4px) scale(0.85)',
      },
    },
    {
      props: { variant: 'filled', size: 'small' },
      style: {
        transform: 'translate(12px, 13px) scale(1)',
      },
    },
    {
      props: ({ variant, ownerState, size }) => variant === 'filled' && !!ownerState.shrink && size === 'small',
      style: {
        transform: 'translate(12px, 3px) scale(0.85)',
      },
    },
  ],
};

export const MuiFilledInput: Components<Theme>['MuiFilledInput'] = {
  styleOverrides: {
    input: {
      variants: [
        {
          props: ({ ownerState }) => !ownerState.multiline,
          style: filledInputSingleLineStyles.medium,
        },
        {
          props: ({ ownerState }) => !ownerState.multiline && !!ownerState.hiddenLabel,
          style: filledInputSingleLineStyles.mediumHiddenLabel,
        },
        {
          props: ({ ownerState, size }) => !ownerState.multiline && size === 'small',
          style: filledInputSingleLineStyles.small,
        },
        {
          props: ({ ownerState, size }) => !ownerState.multiline && !!ownerState.hiddenLabel && size === 'small',
          style: filledInputSingleLineStyles.smallHiddenLabel,
        },
      ],
    },
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      [`&, &.${filledInputClasses.focused}, &.${filledInputClasses.disabled}, &:hover`]: {
        backgroundColor: themeVars(theme).palette.action.enabled,
        '@media (hover: none)': {
          backgroundColor: themeVars(theme).palette.action.enabled,
        },
      },
      // Autofill overrides for filled inputs at root level
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${themeVars(theme).palette.background.level1} inset !important`,
        WebkitTextFillColor: `${themeVars(theme).palette.text.primary} !important`,
        caretColor: themeVars(theme).palette.text.primary,
        borderRadius: 'inherit',
      },
      '& input:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 100px ${themeVars(theme).palette.background.level1} inset !important`,
      },
      '& input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${themeVars(theme).palette.background.level1} inset !important`,
      },
    }),
  },
  variants: [
    {
      props: ({ ownerState }) => !ownerState.disableUnderline,
      style: ({ theme }) =>
        getFilledInputUnderlineStyles({
          theme,
          classes: {
            focused: filledInputClasses.focused,
            disabled: filledInputClasses.disabled,
            error: filledInputClasses.error,
          },
        }),
    },

    // Multiline - medium
    {
      props: ({ ownerState }) => !!ownerState.multiline,
      style: ({ theme }) => ({
        padding: '25px 12px 8px',
        // Scrollbar should stick to the edge of the input
        paddingRight: 0,
        '& textarea': {
          paddingRight: theme.spacing(1.5),
        },
      }),
    },
    {
      props: ({ ownerState }) => !!ownerState.multiline && !!ownerState.hiddenLabel,
      style: {
        paddingTop: 12,
        paddingBottom: 11,
      },
    },

    // Multiline - small
    {
      props: ({ ownerState, size }) => !!ownerState.multiline && size === 'small',
      style: {
        paddingTop: 15,
        paddingBottom: 2,
      },
    },
    {
      props: ({ ownerState }) => !!ownerState.multiline && !!ownerState.hiddenLabel && ownerState.size === 'small',
      style: {
        paddingTop: 9,
        paddingBottom: 9,
      },
    },
  ],
};

export const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
  styleOverrides: {
    input: ({ theme }) => ({
      ...theme.typography.label2,
      fontWeight: theme.typography.fontWeightMedium,
    }),
    notchedOutline: ({ theme }) => ({
      borderColor: themeVars(theme).palette.border.subtle,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
    }),
  },
};
